import { CircleArrowLeft } from 'lucide-react'
import type * as React from 'react'
import { useLocation, useMatches } from 'react-router'
import { NavUser } from '#app/components/nav-user'
import {
	Sidebar,
	SidebarContent,
	// SidebarEdgeToggle, // reverted to the SiteHeader's SidebarTrigger
	SidebarFooter,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarTrigger,
} from '#app/components/ui/sidebar'
import { assets } from '#app/data/assets'
import { useUserRoles } from '#app/hooks/useUserRoles'
import { createAvatarName } from '#app/utils/avatar-name'
import { getPrimaryRole } from '#app/utils/dashboardUtils'
import type { ModelName } from '#app/utils/modelNames'
import { models } from '#app/utils/models'
import type { Theme } from '#app/utils/theme.server'
// import { usePRDData } from '#app/utils/useProjectData'
import { useOptionalUser } from '#app/utils/user'
import LoginLogoSmall from '../assets/TrueTenentSmall.svg'
import {
	type DashboardNavItem,
	getDashboardNavItems,
	getRoleDisplayInfo,
	secondaryNavItems,
} from './enhanced-dashboard-sidebar-items'
import { NavItems } from './nav-items'
import type { UnifiedIconName } from './ui/unified-icon'

const internalModels: ModelName[] = ['prd']
interface NavItem {
	icon: UnifiedIconName
	inProject: boolean
	isActive: boolean
	name: string
	url: string
}

export function internalNavItems(prdId: string): NavItem[] {
	const location = useLocation()

	const items = internalModels.map((modelName) => {
		const model = models[modelName]
		const url = model.inProject ? model.listUrl() : model.listUrl()
		return {
			icon: ('icon' in model ? model.icon : 'check') as UnifiedIconName,
			inProject: true,
			isActive:
				location.pathname.toLowerCase() === url.toLowerCase() ||
				location.pathname.toLowerCase().startsWith(url.toLowerCase()),
			name: model.displayNames.plural,
			url,
		}
	})

	return items
}

function CodeItem({ prdId }: { prdId: string }): NavItem {
	const location = useLocation()
	const url = `/prds/${prdId}/code`
	return {
		icon: 'code',
		inProject: false,
		isActive: location.pathname === url || location.pathname.startsWith(url),
		name: 'Code',
		url,
	}
}

const secondaryData: {
	icon: UnifiedIconName
	name: string
	primary?: boolean
	url: string
}[] = [
	{ icon: 'message-square-text', name: 'Feedback', url: '/contact' },
	{ icon: 'star', name: 'Upgrade to Pro', primary: true, url: '/pricing' },
]

export function AppSidebar({
	theme,
	variant = 'inset',
	...props
}: React.ComponentProps<typeof Sidebar> & {
	theme: null | Theme
	variant?: 'sidebar' | 'floating' | 'inset'
}) {
	const user = useOptionalUser()
	const location = useLocation()
	const matches = useMatches()
	const { roles, permissions, userType, accessibleModules, loading } =
		useUserRoles()

	// During logout, React Router revalidates the root loader (user → null)
	// while the internal route — and therefore this sidebar — is still mounted
	// for one render, before the redirect to a public page commits. Reading the
	// user via useUser() here would throw on that frame and trip the error
	// boundary. Render nothing until the user resolves; the redirect lands next.
	if (!user) return null

	// Get navigation items based on user roles and permissions
	// Don't show any navigation items while loading
	const dashboardNavItems = loading
		? []
		: getDashboardNavItems(
				roles,
				permissions,
				userType,
				new Set(accessibleModules),
			)

	// Get primary role for display
	const primaryRole = loading ? null : getPrimaryRole(roles, userType)
	const roleInfo = primaryRole ? getRoleDisplayInfo(primaryRole) : null

	// Portal title: prefer the user's most specific role name for display
	const portalDisplayName = loading
		? null
		: (() => {
				const displayRole = roles.find((r) => r !== 'landlord') ?? roles[0]
				if (!displayRole) return 'User'
				const info = getRoleDisplayInfo(displayRole)
				if (info) return info.name
				// Custom role fallback: snake_case → Title Case
				return displayRole
					.replace(/_/g, ' ')
					.replace(/\b\w/g, (c) => c.toUpperCase())
			})()

	// Add setup link only if user has no roles at all (system setup needed)
	const shouldShowSetupLink = !loading && roles.length === 0

	// Add setup navigation item if needed
	const navigationItems = shouldShowSetupLink
		? [
				...dashboardNavItems,
				{
					icon: 'reset' as const,
					name: '🚀 Setup SuperAdmin',
					url: '/setup-superadmin',
					badge: 'NEW',
					category: 'setup',
				},
			]
		: dashboardNavItems

	// Application pages that LIVE under a property URL
	// (/properties/:id/applications...) belong to the "All Application"
	// section — the site header's breadcrumb already says "All Application ›
	// Application Detail". Plain prefix matching lit Properties instead
	// (QA 2026-07-18), so those paths re-map to the applications item.
	const inPropertyApplications =
		/^\/properties\/[^/]+\/applications(\/|$)/.test(location.pathname)
	const isItemActive = (url: string) => {
		if (inPropertyApplications) {
			if (url === '/properties') return false
			if (url === '/landlord/applications') return true
		}
		// Path boundary so `/settings` does not light up on `/landlord/settings/...`
		return location.pathname === url || location.pathname.startsWith(`${url}/`)
	}

	// Add isActive property to navigation items (+ child sub-nav active state)
	// Superadmin ▸ LMR Operations wears the count of open LMR alerts — the
	// superadmin layout's loader provides it; elsewhere there is no badge
	const lmrAlerts =
		matches
			.map((m) => m.data)
			.find(
				(d): d is { lmrAlerts: { count: number } } =>
					typeof d === 'object' &&
					d !== null &&
					'lmrAlerts' in d &&
					typeof (d.lmrAlerts as { count?: unknown })?.count === 'number',
			)?.lmrAlerts.count ?? 0
	const withAlerts = navigationItems.map((item) =>
		item.url === '/superadmin/lmr-operations' && lmrAlerts > 0
			? { ...item, badge: lmrAlerts }
			: item,
	)

	const navItemsWithActive = withAlerts.map((item) => ({
		...item,
		isActive: isItemActive(item.url),
		children: item.children?.map((c) => ({
			...c,
			isActive: location.pathname === c.url,
		})),
	}))

	// Convert to NavItem format for compatibility
	const convertedNavItems = navItemsWithActive.map((item) => ({
		...item,
		isActive: item.isActive,
		comingSoon: item.comingSoon,
	}))

	const convertedSecondaryItems = secondaryNavItems.map((item) => ({
		...item,
		isActive:
			location.pathname === item.url || location.pathname.startsWith(item.url),
		primary: item.name === 'Upgrade Plan',
	}))

	// Shimmer loading component
	const SidebarShimmer = () => (
		<div className="space-y-2">
			{Array.from({ length: 4 }).map((_, i) => (
				<div key={i} className="flex items-center space-x-3 px-3 py-2">
					<div className="h-4 w-4 animate-pulse rounded bg-gray-200" />
					<div className="h-4 flex-1 animate-pulse rounded bg-gray-200" />
				</div>
			))}
		</div>
	)

	return (
		<Sidebar collapsible="icon" variant={variant} {...props}>
			{/* Exactly the SiteHeader's h-12 (client 2026-08-27): the button's
			    py-2 around the 32px logo already makes 48px, so the default
			    p-0.5 here is what pushed the first nav item below the header's
			    bottom edge. */}
			<SidebarHeader className="h-12 justify-center p-0">
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton asChild size="lg" tooltip="TrueTenant">
							<a
								href="/"
								className="px-2 py-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-2"
							>
								<div className="group-data-[collapsible=icon]:hidden">
									{/* Top row: Logo + Arrow */}
									<div className="flex items-center justify-between">
										<img
											src={assets.logos.main}
											alt="TrueTenant Logo"
											className="h-8 w-[166px] object-contain object-left"
										/>
									</div>

									{/* Subtitle */}
									{/* <span className="mt-1 block text-xs font-medium text-[#32302F]">
										{loading ? (
											<div className="h-3 w-20 animate-pulse rounded bg-gray-300" />
										) : (
											`${portalDisplayName || 'User'} Portal`
										)}
									</span> */}
								</div>
								<div className="hidden group-data-[collapsible=icon]:block">
									{/* Top row: Logo + Arrow */}
									<div className="flex items-center justify-between">
										<img
											src={LoginLogoSmall}
											alt="TrueTenant Logo"
											className="mx-auto h-8 w-8"
										/>
									</div>
								</div>
							</a>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarHeader>
			{/* Edge toggle commented out — the collapse/expand control lives in the
			    SiteHeader again (SidebarTrigger), which also drives the mobile menu.
			    <SidebarEdgeToggle /> */}
			<SidebarContent>
				{loading ? (
					<SidebarShimmer />
				) : (
					<>
						<div className="sidebar-scroll min-h-0 flex-1 [scrollbar-width:thin] overflow-y-auto">
							<NavItems items={convertedNavItems} />
						</div>
						<NavItems
							className="mt-auto shrink-0 border-t-0"
							items={convertedSecondaryItems}
						/>
					</>
				)}
			</SidebarContent>
			{/* <SidebarFooter>
				<NavUser
					theme={theme}
					user={{ ...user, avatar: createAvatarName(user.name ?? '') }}
				/>
			</SidebarFooter> */}
			{/* Desktop collapse/expand control, bottom-right of the sidebar — the
			    SiteHeader's trigger is now mobile-only (md:hidden) and keeps
			    driving the menu Sheet there. Hidden on mobile: inside the Sheet
			    it would only close the Sheet. Centered when icon-collapsed (the
			    3rem rail leaves no meaningful "right"). */}
			<SidebarFooter className="hidden shrink-0 md:flex">
				<SidebarTrigger className="ml-auto size-8 rounded-full text-[#32302F] hover:bg-[#EAF0F5] group-data-[collapsible=icon]:mx-auto" />
			</SidebarFooter>
		</Sidebar>
	)
}
